/**
 * "Contact us for bulk pricing." — a short enquiry form in the page's modal.
 *
 * POST /api/v1/ip-intel/bulk-enquiry stores the request and e-mails it to
 * info@universitytech.io with the visitor on CC (and as Reply-To), so the
 * team's reply-all keeps them in the thread. The server re-validates every
 * field and answers a bad one with `field`, shown inline here. A draft is
 * kept for the tab, so closing the form by mistake loses nothing.
 */

import React, { useEffect, useRef, useState } from 'react';
import { apiService } from '../../../services/apiService';
import { IprModal, ModalCloseVia } from './Modal';
import { BulkField, ipIntelApi, visitorIds } from './api';
import { BulkPosition, ipClip, ipTrack } from './track';
import { CONTACT_EMAIL, EMAIL_RE } from './content';

const DRAFT_KEY = 'ipintel_bulk_draft';
const LEAD_KEY = 'ipintel_lead';     // the sample-download form's details, if given this visit
export const BULK_MSG_MIN = 10;
export const BULK_MSG_MAX = 4000;
const NAME_MAX = 80;
const EMAIL_MAX = 254;

type Fields = Record<BulkField, string>;
const EMPTY: Fields = { first_name: '', last_name: '', email: '', message: '' };
const ORDER: BulkField[] = ['first_name', 'last_name', 'email', 'message'];
const ID: Record<BulkField, string> = {
  first_name: 'ipr-bulk-first', last_name: 'ipr-bulk-last', email: 'ipr-bulk-email', message: 'ipr-bulk-message',
};

const readJson = (k: string): any => { try { return JSON.parse(sessionStorage.getItem(k) || 'null'); } catch { return null; } };

const initial = (): Fields => {
  const draft = readJson(DRAFT_KEY) || {};
  const lead = readJson(LEAD_KEY) || {};
  const pick = (k: BulkField) => String(draft[k] || (k === 'message' ? '' : lead[k]) || '');
  return { first_name: pick('first_name'), last_name: pick('last_name'), email: pick('email'), message: pick('message') };
};

export const validateBulk = (f: Fields): Partial<Record<BulkField, string>> => {
  const e: Partial<Record<BulkField, string>> = {};
  const first = f.first_name.trim();
  const last = f.last_name.trim();
  const email = f.email.trim();
  const msg = f.message.trim();
  if (!first) e.first_name = 'Please enter your first name.';
  else if (first.length > NAME_MAX) e.first_name = `Please keep it under ${NAME_MAX} characters.`;
  if (!last) e.last_name = 'Please enter your last name.';
  else if (last.length > NAME_MAX) e.last_name = `Please keep it under ${NAME_MAX} characters.`;
  if (!EMAIL_RE.test(email) || email.length > EMAIL_MAX) e.email = 'Please enter a valid e-mail address.';
  if (msg.length < BULK_MSG_MIN) e.message = `A little more detail, please — at least ${BULK_MSG_MIN} characters.`;
  else if (msg.length > BULK_MSG_MAX) e.message = `Please keep the message under ${BULK_MSG_MAX.toLocaleString('en-US')} characters.`;
  return e;
};

interface Props {
  position: BulkPosition;
  onClose: () => void;
}

export const BulkEnquiryModal: React.FC<Props> = ({ position, onClose }) => {
  const [f, setF] = useState<Fields>(initial);
  const [hp, setHp] = useState('');
  const [errors, setErrors] = useState<Partial<Record<BulkField, string>>>({});
  const [formError, setFormError] = useState('');
  const [phase, setPhase] = useState<'form' | 'sending' | 'done'>('form');
  const [done, setDone] = useState<{ ref: string; email: string; message: string } | null>(null);
  const sendingRef = useRef(false);
  const signedInId = apiService.getStoredUserId?.() || '';
  const dirty = ORDER.some(k => f[k].trim() !== '');

  // Prefill from the signed-in profile, only into fields still empty.
  useEffect(() => {
    if (!signedInId) return;
    let live = true;
    apiService.getProfile(signedInId).then(res => {
      if (!live || !res.success || !res.data) return;
      const u: any = (res.data as any).user || {};
      setF(prev => ({
        ...prev,
        first_name: prev.first_name || String(u.first_name || ''),
        last_name: prev.last_name || String(u.last_name || ''),
        email: prev.email || String(u.email || ''),
      }));
    }).catch(() => { /* prefill is a convenience */ });
    return () => { live = false; };
  }, [signedInId]);

  useEffect(() => {
    if (phase === 'done') return;
    try { sessionStorage.setItem(DRAFT_KEY, JSON.stringify(f)); } catch { /* ignore */ }
  }, [f, phase]);

  const close = (via: ModalCloseVia | 'done') => {
    if (phase === 'sending') return;
    ipTrack('ipintel_bulk_close', { via, stage: phase === 'done' ? 'success' : 'form', dirty, position });
    onClose();
  };

  const set = (k: BulkField) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const v = e.target.value;
    setF(prev => ({ ...prev, [k]: v }));
    if (errors[k]) setErrors(prev => ({ ...prev, [k]: undefined }));
    if (formError) setFormError('');
  };

  const focusField = (k: BulkField) => setTimeout(() => document.getElementById(ID[k])?.focus(), 0);

  const submit = async () => {
    if (sendingRef.current) return;
    const errs = validateBulk(f);
    setErrors(errs);
    setFormError('');
    const bad = ORDER.filter(k => errs[k]);
    if (bad.length) {
      ipTrack('ipintel_bulk_error', { stage: 'client', error: 'validation', fields: bad.join(','), position });
      focusField(bad[0]);
      return;
    }
    sendingRef.current = true;
    setPhase('sending');
    const body = {
      first_name: f.first_name.trim(),
      last_name: f.last_name.trim(),
      email: f.email.trim().toLowerCase(),
      message: f.message.trim(),
      website: hp,
      page_url: (() => { try { return window.location.href.slice(0, 500); } catch { return undefined; } })(),
      ...visitorIds(),
      // With the bearer this ties the enquiry to the account (verified server-side).
      ...(signedInId ? { user_id: signedInId } : {}),
    };
    ipTrack('ipintel_bulk_submit', { position, message_len: body.message.length, signed_in: !!signedInId });
    const res = await ipIntelApi.bulkEnquiry(body);
    sendingRef.current = false;
    if (res.ok) {
      ipTrack('ipintel_bulk_success', { position, ref: res.ref });
      try { sessionStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
      setDone({ ref: res.ref, email: body.email, message: body.message });
      setPhase('done');
      return;
    }
    setPhase('form');
    ipTrack('ipintel_bulk_error', {
      stage: 'server', status: res.status, field: res.field || null,
      error: ipClip(res.error, 120), position,
    });
    if (res.error === 'rate_limited') {
      setFormError(`Too many requests from your network just now. Please wait a little and try again — or write to ${CONTACT_EMAIL} directly.`);
      return;
    }
    if (res.field) {
      setErrors({ [res.field]: res.error } as Partial<Record<BulkField, string>>);
      focusField(res.field);
      return;
    }
    setFormError(res.status >= 500
      ? `Something went wrong on our side and your request was not sent. Please try again in a moment — or write to ${CONTACT_EMAIL}.`
      : res.error);
  };

  const busy = phase === 'sending';
  const msgLen = f.message.trim().length;
  const over = msgLen > BULK_MSG_MAX;

  const input = (k: BulkField, label: string, type: string, auto: string, placeholder: string) => (
    <div>
      <label className="ipr-label" htmlFor={ID[k]}>{label}</label>
      <input
        id={ID[k]}
        type={type}
        className={`ipr-input${errors[k] ? ' is-invalid' : ''}`}
        value={f[k]}
        onChange={set(k)}
        autoComplete={auto}
        placeholder={placeholder}
        maxLength={k === 'email' ? EMAIL_MAX : NAME_MAX}
        disabled={busy}
        aria-invalid={!!errors[k]}
        aria-describedby={errors[k] ? `${ID[k]}-err` : undefined}
        required
      />
      {errors[k] && <div id={`${ID[k]}-err`} className="ipr-field-error" role="alert">{errors[k]}</div>}
    </div>
  );

  return (
    <IprModal labelledBy="ipr-bulk-title" onClose={via => close(via)} locked={busy} maxWidth={500}>
      {phase === 'done' && done ? (
        <div role="status">
          <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--ipr-success-bg)', color: 'var(--ipr-success-400)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, marginBottom: 18 }} aria-hidden="true">✓</div>
          <div className="ipr-eyebrow" style={{ color: 'var(--ipr-primary-600)', marginBottom: 10 }}>Request sent</div>
          <div id="ipr-bulk-title" style={{ fontFamily: 'var(--ipr-font-display)', fontSize: 22, fontWeight: 700, color: 'var(--ipr-heading)', marginBottom: 10 }}>
            We have your request
          </div>
          <p style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--ipr-text)', margin: '0 0 16px' }}>
            Thanks — we've sent your request to <strong style={{ color: 'var(--ipr-heading)' }}>{CONTACT_EMAIL}</strong> and
            copied you at <strong style={{ color: 'var(--ipr-heading)', wordBreak: 'break-all' }}>{done.email}</strong>.
            Reference <strong style={{ color: 'var(--ipr-heading)' }}>{done.ref}</strong>.
          </p>
          <div className="ipr-eyebrow" style={{ color: 'var(--ipr-muted)', fontSize: 11, marginBottom: 8 }}>Your message</div>
          <blockquote className="ipr-quote">{done.message}</blockquote>
          <button type="button" className="ipr-btn-primary" style={{ width: '100%', marginTop: 24 }} onClick={() => close('done')}>Done</button>
        </div>
      ) : (
        <>
          <div className="ipr-eyebrow" style={{ color: 'var(--ipr-primary-600)', marginBottom: 10 }}>Bulk pricing</div>
          <div id="ipr-bulk-title" style={{ fontFamily: 'var(--ipr-font-display)', fontSize: 22, fontWeight: 700, color: 'var(--ipr-heading)', marginBottom: 8 }}>
            More than one technology to assess?
          </div>
          <p style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--ipr-text)', margin: '0 0 22px' }}>
            Tell us roughly how many technologies, which fields and any timeline. Your request goes to {CONTACT_EMAIL}, and we copy you on the e-mail.
          </p>
          <form noValidate onSubmit={e => { e.preventDefault(); void submit(); }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div className="ipr-grid-2">
                {input('first_name', 'First name', 'text', 'given-name', 'Ada')}
                {input('last_name', 'Last name', 'text', 'family-name', 'Lovelace')}
              </div>
              {input('email', 'Email for our reply', 'email', 'email', 'you@company.com')}
              <div>
                <label className="ipr-label" htmlFor={ID.message}>Message</label>
                <textarea
                  id={ID.message}
                  className={`ipr-input${errors.message ? ' is-invalid' : ''}`}
                  value={f.message}
                  onChange={set('message')}
                  rows={5}
                  placeholder="e.g. We have 12 technologies across medtech and materials and would like reports over the next quarter."
                  disabled={busy}
                  aria-invalid={!!errors.message}
                  aria-describedby={`ipr-bulk-message-count${errors.message ? ' ipr-bulk-message-err' : ''}`}
                  required
                />
                <div id="ipr-bulk-message-count" className={`ipr-counter${over ? ' is-over' : ''}`} aria-live="polite">
                  <span>{msgLen < BULK_MSG_MIN && !errors.message ? `At least ${BULK_MSG_MIN} characters` : ' '}</span>
                  <span>{msgLen.toLocaleString('en-US')} / {BULK_MSG_MAX.toLocaleString('en-US')}</span>
                </div>
                {errors.message && <div id="ipr-bulk-message-err" className="ipr-field-error" role="alert">{errors.message}</div>}
              </div>
              {/* Honeypot: invisible to people, irresistible to form-filling bots. */}
              <div className="ipr-hp" aria-hidden="true">
                <label htmlFor="ipr-bulk-website">Website</label>
                <input id="ipr-bulk-website" name="website" tabIndex={-1} autoComplete="off" value={hp} onChange={e => setHp(e.target.value)} />
              </div>
            </div>
            {formError && <div className="ipr-alert ipr-alert-error" role="alert" style={{ marginTop: 16 }}>{formError}</div>}
            <button type="submit" className="ipr-btn-primary" style={{ width: '100%', marginTop: 22 }} disabled={busy}>
              {busy && <span className="ipr-spin" aria-hidden="true" />}
              {busy ? 'Sending…' : 'Send request'}
            </button>
          </form>
          <div style={{ fontSize: 12, color: 'var(--ipr-muted)', textAlign: 'center', marginTop: 12 }}>
            Used only to reply to this request.
          </div>
        </>
      )}
    </IprModal>
  );
};
